import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon, palette } from '@m12s/component-library';
import { times } from 'lib/icons';

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 2px;
  min-height: 2.5rem;
  width: 100%;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.5rem;
  background-color: ${({ isSelected }) => {
    return isSelected ? palette.Blue100 : palette.Grey100;
  }};
  border: 2px solid
    ${({ isSelected }) => {
      return isSelected ? palette.Blue500 : palette.Grey300;
    }};
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${palette.Grey800};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${({ isSelected }) => {
      return isSelected ? palette.Blue100 : palette.Grey200;
    }};
    border-color: ${({ isSelected }) => {
      return isSelected ? palette.Blue500 : palette.Grey400;
    }};
  }

  &:hover .close-button {
    opacity: 1;
  }
`;

const ChipLabel = styled.span`
  color: ${palette.Grey600};
  font-weight: 600;
`;

const ChipValue = styled.span`
  color: ${palette.Grey800};
  font-weight: 500;
`;

const ChipInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${palette.Grey800};
  padding: 0;
  margin: 0;
  width: ${({ inputWidth }) => {
    return inputWidth || '100px';
  }};
  min-width: 50px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  margin-left: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: ${palette.Grey600};

  &:hover {
    color: ${palette.Grey800};
  }
`;

const FIELD_DISPLAY_NAMES = {
  workOrderId: 'Work Order',
  sequenceNumber: 'Seq',
  lot: 'Lot',
  sub: 'Sub',
  split: 'Split',
};

const BarcodeFieldChip = ({
  fieldName,
  fieldValue,
  isSelected,
  onSelect,
  onChange,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(fieldValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(fieldValue);
  }, [fieldValue]);

  const handleChipClick = (e) => {
    e.stopPropagation();
    if (!isEditing) {
      onSelect(fieldName);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    if (editValue.trim() && editValue !== fieldValue) {
      onChange(fieldName, editValue.trim());
    } else {
      setEditValue(fieldValue);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditValue(fieldValue);
      setIsEditing(false);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(fieldName);
  };

  const displayName = FIELD_DISPLAY_NAMES[fieldName] || fieldName;
  const inputWidth = `${Math.max(editValue.length * 8, 50)}px`;

  return (
    <Chip
      isSelected={isSelected}
      onClick={handleChipClick}
      onDoubleClick={handleDoubleClick}
    >
      <ChipLabel>{displayName}:</ChipLabel>
      {isEditing ? (
        <ChipInput
          ref={inputRef}
          value={editValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          inputWidth={inputWidth}
          onClick={(e) => {
            return e.stopPropagation();
          }}
        />
      ) : (
        <ChipValue>{fieldValue}</ChipValue>
      )}
      <CloseButton className="close-button" onClick={handleDelete}>
        <Icon icon={times} size="0.75rem" />
      </CloseButton>
    </Chip>
  );
};

BarcodeFieldChip.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldValue: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const BarcodeFieldChips = ({
  fields,
  selectedField,
  onFieldSelect,
  onFieldChange,
  onFieldDelete,
  fieldOrder,
}) => {
  const handleContainerClick = () => {
    // Deselect when clicking empty space
    if (selectedField) {
      onFieldSelect(null);
    }
  };

  // Order fields based on fieldOrder array
  const orderedFieldNames = fieldOrder
    ? fieldOrder.filter((field) => {
        return fields[field];
      })
    : Object.keys(fields);

  return (
    <ChipsContainer onClick={handleContainerClick}>
      {orderedFieldNames.map((fieldName) => {
        return (
          <BarcodeFieldChip
            key={fieldName}
            fieldName={fieldName}
            fieldValue={fields[fieldName]}
            isSelected={selectedField === fieldName}
            onSelect={onFieldSelect}
            onChange={onFieldChange}
            onDelete={onFieldDelete}
          />
        );
      })}
    </ChipsContainer>
  );
};

BarcodeFieldChips.propTypes = {
  fields: PropTypes.object.isRequired,
  selectedField: PropTypes.string,
  onFieldSelect: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFieldDelete: PropTypes.func.isRequired,
  fieldOrder: PropTypes.arrayOf(PropTypes.string),
};

export { BarcodeFieldChips };
